import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, Scale, HeartHandshake, Lock, FlaskConical } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { AppStoreBadge } from "@/components/AppStoreBadge";
import { GooglePlayBadge } from "@/components/GooglePlayBadge";

const VALUES = [
  {
    icon: Scale,
    color: "bg-[#1FBCA2]",
    title: "Fair by design",
    body: "Rules enforced by the app, not the loudest adult at the table. If a mechanic can be gamed, we redesign it.",
  },
  {
    icon: HeartHandshake,
    color: "bg-[#FFC837]",
    title: "Family first",
    body: "Everything we ship is reviewed for ages 6+. No ads, no dark patterns, no 'just one more purchase' traps.",
  },
  {
    icon: Lock,
    color: "bg-[#FF7B72]",
    title: "Privacy always",
    body: "We collect the minimum, sell nothing, and delete on request. Your family's data is not the business model.",
  },
];

export default function About() {
  return (
    <main data-testid="about-page" className="mx-auto max-w-3xl px-6 pb-28 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="pt-20 md:pt-28"
      >
        <p className="inline-flex items-center gap-2 rounded-full border-2 border-[#1A1D1E] bg-[#1FBCA2] px-5 py-2 text-xs font-extrabold uppercase tracking-[0.2em] text-[#1A1D1E] shadow-[0_4px_0_0_#1A1D1E]">
          <FlaskConical className="h-4 w-4" />
          The team behind TriviaPro
        </p>
        <h1 className="mt-8 font-display text-5xl font-bold tracking-tight md:text-6xl">
          About TriviaLabs
        </h1>
        <p className="mt-5 max-w-xl text-base leading-relaxed text-[#6B7280] md:text-lg">
          TriviaLabs is a small independent team that makes games that pull
          families and friends onto the same couch. We believe the best
          screen time is the kind you share.
        </p>
      </motion.div>

      <Reveal className="mt-14">
        <div className="rounded-[20px] border-2 border-[#1A1D1E] bg-[#FFC837] p-8 shadow-[0_6px_0_0_#1A1D1E] md:p-10">
          <p className="font-display text-2xl font-semibold leading-snug tracking-tight text-[#1A1D1E] md:text-3xl">
            "I created TriviaPro because the trivia apps out there were full
            of ads, the questions were boring walls of text, and family play
            wasn't easy. So I built the app my own family actually wanted."
          </p>
          <p className="mt-4 text-sm font-extrabold uppercase tracking-[0.2em] text-[#1A1D1E]/60">
            Matt Willson – Founder, TriviaLabs
          </p>
        </div>
      </Reveal>

      <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-3">
        {VALUES.map((v, i) => (
          <Reveal key={v.title} delay={i * 0.08}>
            <div
              data-testid={`about-value-${i + 1}`}
              className="h-full rounded-[20px] border-2 border-[#1A1D1E] bg-white p-6 shadow-[0_6px_0_0_#1A1D1E]"
            >
              <span className={`mb-4 grid h-11 w-11 place-items-center rounded-[12px] border-2 border-[#1A1D1E] ${v.color}`}>
                <v.icon className="h-5 w-5 text-[#1A1D1E]" />
              </span>
              <h2 className="font-display text-xl font-semibold tracking-tight">{v.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-[#6B7280]">{v.body}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-10">
        <div data-testid="about-app-card" className="rounded-[20px] border-2 border-[#1A1D1E] bg-white p-8 shadow-[0_6px_0_0_#1A1D1E] md:p-10">
          <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-[#1FBCA2]">
            Our apps
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-5">
            <img
              src="/app-icon.jpg"
              alt="TriviaPro app icon"
              className="h-20 w-20 rounded-[20px] border-2 border-[#1A1D1E] object-cover shadow-[0_4px_0_0_#1A1D1E]"
            />
            <div>
              <h2 className="font-display text-2xl font-semibold tracking-tight">TriviaPro</h2>
              <p className="mt-1 max-w-sm text-sm leading-relaxed text-[#6B7280]">
                Fair-play family trivia for kids and adults, ages 6+.
                Age-matched questions, sound rounds, and a fun fact after
                every answer.
              </p>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-4">
            <AppStoreBadge testId="about-app-store-badge" />
            <GooglePlayBadge testId="about-google-play-badge" />
          </div>
          <p className="mt-4 text-xs font-bold text-[#6B7280]">
            <Link to="/privacy" data-testid="about-privacy-link" className="underline decoration-[#1FBCA2] decoration-2 underline-offset-4 transition-colors duration-200 hover:text-[#1FBCA2]">
              Read our Privacy Policy
            </Link>
          </p>
        </div>
      </Reveal>

      <Reveal className="mt-10">
        <div className="rounded-[20px] border-2 border-[#1A1D1E] bg-[#1A1D1E] p-8 text-center shadow-[0_6px_0_0_rgba(26,29,30,0.3)] md:p-10">
          <h2 className="font-display text-2xl font-semibold tracking-tight text-[#FAF9F6]">
            Say hello
          </h2>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-[#FAF9F6]/60">
            Feedback, press, partnerships, or a question category we absolutely
            must add – we read everything.
          </p>
          <a
            href="mailto:info@trivialabs.org"
            data-testid="about-contact-email"
            className="mt-6 inline-flex items-center gap-3 rounded-full border-2 border-[#1A1D1E] bg-[#FFC837] px-7 py-3.5 font-display text-base font-semibold text-[#1A1D1E] shadow-[0_5px_0_0_rgba(250,249,246,0.25)] transition-[transform,box-shadow] duration-150 hover:translate-y-[2px] hover:shadow-[0_3px_0_0_rgba(250,249,246,0.25)] active:translate-y-[5px] active:shadow-none focus:outline-none focus-visible:ring-4 focus-visible:ring-[#FFC837]/60"
          >
            <Mail className="h-5 w-5" />
            info@trivialabs.org
          </a>
        </div>
      </Reveal>
    </main>
  );
}
